import "lib/Init";
import { createApp, reactive, ref } from 'vue';
import { useForm, useField } from "vee-validate";
import * as yup from 'yup';
import VueExtensions from 'lib/VueExtensions';
import { __, setOldValuesForDict } from "lib/Utilities";
import { isEmpty as _isEmpty } from "lodash";
import analyticsService from "lib/workperks/services/AnalyticsService";
import { TrackingAction } from "lib/workperks/models/Analytics";
import ProgressArc from 'components/auth/ProgressArc.vue';
import Stepper from 'components/auth/Stepper.vue';
import Popper from "vue3-popper";

const TOOLTIP_HIDE_TIMEOUT = 5;

const normalizeEmail = (value?: string | null) => {
    if (typeof value !== 'string') {
        return value;
    }

    return value.trim().toLowerCase();
};

const locale = window.l?.locale || 'en';
const messages = {
    en: {
        required: (field) => `The ${field} is required.`,
        email: (field) => `The ${field} must be a valid email.`,
        confirmed: "The email address does not match.",
        domain: "The email address does not match approved domains.",
        regex: "The unique code must match the required format",
        length: (field, min, max) => `The ${field} must be between ${min} and ${max} characters`,
    },
    fr: {
        required: (field) => `Le ${field} est requis.`,
        email: (field) => `L'${field} doit être une adresse e-mail valide.`,
        confirmed: "L'adresse e-mail ne correspond pas.",
        domain: "Votre courriel n'apartient pas a une domaines approuvées.",
        regex: "Le code unique doit correspondre au format requis",
        length: (field, min, max) => `Le ${field} doit être entre ${min} et ${max} caractères`,
    }
};

const app = createApp({
    setup() {
        const data = reactive({
            shouldShowConsentForm: false,
            trackingRegistration: {
                site_id: window.s.site.id,
                family_invited: window.s.isInvited ?? false
            },
            steps: [
                {
                    title: __("steps.step_2.title"),
                    description: __("steps.step_2.description")
                },
                {
                    title: __("steps.step_3.title"),
                    description: __("steps.step_3.description")
                },
                {
                    title: __("steps.step_4.title"),
                    description: __("steps.step_5.description")
                },
            ],
        });

        const prefill = window.s.prefill;
        const validationConfig = window.s.validationConfig || {};

        if (window.s.old && !_isEmpty(window.s.old)) {
            setOldValuesForDict(data, window.s.old ? window.s.old : null);
        } else {
            if (window.s.prefill && !_isEmpty(window.s.prefill)) {
                setOldValuesForDict(data, window.s.prefill ? window.s.prefill : null);
            }
        }

        const schemaFields: Record<string, yup.AnySchema> = {
            email: yup.string()
                .transform((value) => normalizeEmail(value))
                .required(messages[locale].required('Email address'))
                .min(5, messages[locale].length('Email address', 5, 100))
                .max(100, messages[locale].length('Email address', 5, 100))
                .email(messages[locale].email('Email address'))
                .matches(/^(?!.*\.\.)[a-zA-Z0-9._%+'-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, messages[locale].email('Email address'))
                .test('domain', messages[locale].domain, function (value) {
                    if (!value) return true;
                    const normalizedValue = normalizeEmail(value);

                    if (!normalizedValue) {
                        return true;
                    }

                    const emailStruct = normalizedValue.split("@");

                    if (emailStruct.length !== 2) {
                        return false;
                    }

                    const domains = validationConfig.domains;
                    if (domains && domains.length) {
                        return domains.indexOf(emailStruct[1]) > -1;
                    }

                    return true;
                })
                .label(window.l?.locale === 'fr' ? __("create.email_add") : "Email address"),
            verifyEmail: yup.string()
                .transform((value) => normalizeEmail(value))
                .required(messages[locale].required('Email address'))
                .test('matches-email', messages[locale].confirmed, function (value) {
                    return normalizeEmail(value) === normalizeEmail(this.parent.email);
                })
                .label(window.l?.locale === 'fr' ? __("create.email_add") : "Email address")
        };

        if (validationConfig.showCodeField) {
            let codeValidation = yup.string()
                .required(messages[locale].required('Unique Code'))
                .min(1, `The Unique Code must be at least 1 character`)
                .max(255, `The Unique Code must be at most 255 characters`)
                .label(locale === 'fr' ? __("create.unique_code_label") : "Unique Code");

            // Add regex validation if pattern is provided
            if (validationConfig.codePattern) {
                try {
                    const regex = new RegExp(validationConfig.codePattern);
                    codeValidation = codeValidation.matches(regex, messages[locale].regex);
                } catch (e) {
                    console.error('Invalid regex pattern for code validation:', validationConfig.codePattern);
                }
            }

            schemaFields.code = codeValidation;
        }

        const validationSchema = yup.object(schemaFields);
        const initialValues = {
            email: prefill?.email || '',
            verifyEmail: '',
            ...(validationConfig.showCodeField && {
                code: prefill?.code || '',
            }),
        };

        const { handleSubmit, errors } = useForm({
            validationSchema,
            initialValues,
        });

        const { value: code } = validationConfig.showCodeField
            ? useField('code')
            : { value: ref('') };
        const { value: email } = useField('email');
        const { value: verifyEmail } = useField('verifyEmail');

        const popper = ref(null);
        const submitBtn = ref(null);
        const isValidForm = ref(false);

        const onInvalidSubmit = () => {
            submitBtn.value.disabled = false;

            if (errors.value.email && errors.value.email.includes('domain')) {
                popper.value.show();
                setTimeout(() => {
                    popper.value.hide();
                }, 1000 * TOOLTIP_HIDE_TIMEOUT);
            }

            const invalidElement = document.getElementsByClassName("is-invalid")[0];
            if (invalidElement) {
                invalidElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }
        };

        const onValidSubmit = () => {
            // Don't click the button, directly submit the form
            const form = document.getElementById("register__user-form") as HTMLFormElement;

            // Set flag to allow submission
            isValidForm.value = true;

            form.requestSubmit();
        };

        const validateAndSubmit = handleSubmit(onValidSubmit, onInvalidSubmit);

        const formWillSubmit = (event) => {
            if (isValidForm.value) {
                isValidForm.value = false;
                return;
            }

            event.preventDefault();
            submitBtn.value.disabled = true;

            validateAndSubmit();
        };

        const initializeTracking = () => {
            analyticsService
                .initialize(data.trackingRegistration.site_id, null, null)
                .setPageName('GetActivationCode');
        };

        const trackPageView = () => {
            analyticsService.trackPageView({});
        };

        initializeTracking();
        trackPageView();
        analyticsService.trackUserRegistration(TrackingAction.View, data.trackingRegistration, 1, 'GetActivationCode');

        return {
            data,
            code,
            email,
            verifyEmail,
            errors,
            popper,
            submitBtn,
            isValidForm,
            formWillSubmit,  // Use this in template @submit handler
            trackPageView
        };
    },
    directives: {
        lowercase: {
            updated(el) {
                const input = el;
                input.value = input.value.toLowerCase();
                el = input;
            }
        }
    },
    components: {
        'progress-arc': ProgressArc,
        'vgo-stepper': Stepper,
        'v-popper': Popper,
    }
});

app.use(VueExtensions);
app.mount("#app");